import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Filter, Loader2, RefreshCcw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import AdminPageFrame from "../components/admin/AdminPageFrame";
import config from "../config/config";
import { Button } from "../components/ui/button";
import { buildAuthHeaders, getStoredIdentity } from "../utils/authSession";

const STATUS_OPTIONS = ["", "new", "triage", "need_info", "planned", "in_progress", "done", "declined", "reopened"];
const PRIORITY_OPTIONS = ["", "P1", "P2", "P3", "P4"];
const ROLE_OPTIONS = ["", "applicant"];
const MODULE_OPTIONS = ["", "application", "documents", "profile", "account", "language_test_b1"];
const ISSUE_OPTIONS = ["", "technical_error", "unclear_content", "data_incorrect", "something_missing", "improvement_suggestion", "change_request", "other"];
const CHANNEL_OPTIONS = ["", "web"];

function authHeaders() {
  return buildAuthHeaders();
}

function formatDateTime(value) {
  if (!value) return "n/a";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function humanize(value) {
  const map = {
    technical_error: "Technischer Fehler",
    unclear_content: "Inhalt unklar",
    data_incorrect: "Daten stimmen nicht",
    something_missing: "Etwas fehlt",
    improvement_suggestion: "Verbesserungsvorschlag",
    change_request: "Aenderungswunsch",
    other: "Sonstiges",
    blocking: "blockiert",
    difficult: "erschwert",
    hint: "Hinweis",
    yes: "Ja",
    partly: "Teilweise",
    no: "Nein",
    application: "Bewerbung",
    documents: "Unterlagen",
    profile: "Profil",
    account: "Konto",
    language_test_b1: "Sprachtest B1",
  };
  return map[value] || value || "n/a";
}

function humanizeEventType(value) {
  const map = {
    created: "Meldung erstellt",
    attachment_added: "Anhang hinzugefuegt",
    updated: "Fall aktualisiert",
    assigned: "Zuweisung",
    status_changed: "Statuswechsel",
    comment: "Interne Notiz",
    admin_reply: "Antwort an Applicant",
  };
  return map[value] || value || "Event";
}

export default function AdminKvpInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "",
    priority: searchParams.get("priority") || "",
    reporter_role: searchParams.get("reporter_role") || "",
    module_key: searchParams.get("module_key") || "",
    section_key: searchParams.get("section_key") || "",
    issue_type: searchParams.get("issue_type") || "",
    channel: searchParams.get("channel") || "",
    date_from: searchParams.get("date_from") || "",
    date_to: searchParams.get("date_to") || "",
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ open_p1: 0, open_p2: 0, new_today: 0, top_module_7d: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedId, setSelectedId] = useState(() => searchParams.get("report") || "");
  const [statusDraft, setStatusDraft] = useState("triage");
  const [statusComment, setStatusComment] = useState("");
  const [priorityDraft, setPriorityDraft] = useState("P3");
  const [assignedDraft, setAssignedDraft] = useState("");
  const [eventText, setEventText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [attachmentPreviews, setAttachmentPreviews] = useState({});
  const adminUserid = getStoredIdentity().userid;

  useEffect(() => {
    document.title = "Experts4Europe | Admin KVP";
  }, []);

  const selectedReport = detail?.report || null;

  const loadInbox = async (nextFilters = filters) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/admin/kvp/reports`, {
        headers: authHeaders(),
        params: nextFilters,
      });
      const payload = response.data || {};
      const nextRows = Array.isArray(payload.rows) ? payload.rows : [];
      setRows(nextRows);
      setSummary(payload.summary || { open_p1: 0, open_p2: 0, new_today: 0, top_module_7d: null });
      if (!selectedId && nextRows.length) {
        setSelectedId(String(nextRows[0].id));
      } else if (selectedId && !nextRows.some((entry) => String(entry.id) === String(selectedId))) {
        setSelectedId(nextRows[0] ? String(nextRows[0].id) : "");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Die KVP-Inbox konnte nicht geladen werden.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (reportId) => {
    if (!reportId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setDetailError("");
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/admin/kvp/reports/${reportId}`, {
        headers: authHeaders(),
      });
      const report = response.data?.report || null;
      setDetail({ report });
      setStatusDraft(report?.status || "triage");
      setPriorityDraft(report?.priority || "P3");
      setAssignedDraft(report?.assigned_userid || "");
    } catch (err) {
      setDetail(null);
      setDetailError(err?.response?.data?.message || "Die Detailansicht konnte nicht geladen werden.");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadInbox(filters);
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    loadDetail(selectedId);
  }, [selectedId]);

  useEffect(() => {
    const previews = [];
    const attachments = selectedReport?.attachments || [];
    if (!attachments.length) {
      setAttachmentPreviews({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const nextPreviews = {};
      for (const attachment of attachments) {
        if (!String(attachment.mime_type || "").startsWith("image/")) continue;
        try {
          const response = await axios.get(
            `${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}/attachments/${attachment.id}/file`,
            { headers: authHeaders(), responseType: "blob" }
          );
          const previewUrl = URL.createObjectURL(response.data);
          previews.push(previewUrl);
          nextPreviews[attachment.id] = previewUrl;
        } catch (_err) {
          // Preview bleibt dann einfach ein Download-Link.
        }
      }
      if (!cancelled) setAttachmentPreviews(nextPreviews);
    })();
    return () => {
      cancelled = true;
      previews.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    };
  }, [selectedReport?.id]);

  const applyFilters = async () => {
    const nextParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) nextParams.set(key, value);
    });
    if (selectedId) nextParams.set("report", selectedId);
    setSearchParams(nextParams);
    await loadInbox(filters);
  };

  const resetFilters = async () => {
    const next = {
      status: "",
      priority: "",
      reporter_role: "",
      module_key: "",
      section_key: "",
      issue_type: "",
      channel: "",
      date_from: "",
      date_to: "",
    };
    setFilters(next);
    setSearchParams(selectedId ? new URLSearchParams({ report: selectedId }) : new URLSearchParams());
    await loadInbox(next);
  };

  const refreshEverything = async () => {
    await loadInbox(filters);
    if (selectedId) await loadDetail(selectedId);
  };

  const mutateDetail = async (actionKey, request) => {
    if (!selectedId) return;
    setBusyAction(actionKey);
    setDetailError("");
    try {
      await request();
      await loadInbox(filters);
      await loadDetail(selectedId);
      setStatusComment("");
      setEventText("");
      setReplyText("");
    } catch (err) {
      setDetailError(err?.response?.data?.message || "Die Aenderung konnte nicht gespeichert werden.");
    } finally {
      setBusyAction("");
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}/attachments/${attachment.id}/file`,
        { headers: authHeaders(), responseType: "blob" }
      );
      const href = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = attachment.original_name || attachment.file_name || "kvp-attachment";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
    } catch (_err) {
      setDetailError("Der Screenshot konnte nicht geladen werden.");
    }
  };

  const selectedSummaryRow = useMemo(
    () => rows.find((entry) => String(entry.id) === String(selectedId)) || null,
    [rows, selectedId]
  );

  useEffect(() => {
    if (!selectedSummaryRow) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("report", String(selectedSummaryRow.id));
      return next;
    });
  }, [selectedSummaryRow]);

  return (
    <AdminPageFrame
      title="KVP-Inbox"
      subtitle="Kontextbezogene Meldungen aus dem Bewerberbereich mit Priorisierung, Zuweisung und Bearbeitungsverlauf."
      badge="Applicant KVP"
      contentClassName="admin-kvp-page"
      actions={
        <Button className="admin-core-button admin-core-button--secondary" onClick={refreshEverything}>
          <RefreshCcw size={16} />
          <span>Aktualisieren</span>
        </Button>
      }
    >
      <div className="admin-kvp-summary-grid">
        <article className="admin-kvp-summary-card">
          <span>Offene P1</span>
          <strong>{summary.open_p1 || 0}</strong>
          <small>Blockierende Faelle fuer Applicant</small>
        </article>
        <article className="admin-kvp-summary-card">
          <span>Offene P2</span>
          <strong>{summary.open_p2 || 0}</strong>
          <small>Daten- oder Prozessabweichungen</small>
        </article>
        <article className="admin-kvp-summary-card">
          <span>Neu heute</span>
          <strong>{summary.new_today || 0}</strong>
          <small>Seit 00:00 Uhr eingegangen</small>
        </article>
        <article className="admin-kvp-summary-card">
          <span>Haeufigstes Modul 7 Tage</span>
          <strong>{humanize(summary.top_module_7d?.module_key || "n/a")}</strong>
          <small>{summary.top_module_7d?.total || 0} Meldungen</small>
        </article>
      </div>

      <section className="admin-kvp-filterbar">
        <div className="admin-kvp-filterbar__header">
          <div>
            <span className="admin-core-kicker">Filter</span>
            <h3>Inbox eingrenzen</h3>
          </div>
          <Filter size={16} />
        </div>
        <div className="admin-kvp-filter-grid">
          <label><span>Status</span><select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>{STATUS_OPTIONS.map((option) => <option key={option || "all"} value={option}>{option || "alle"}</option>)}</select></label>
          <label><span>Prioritaet</span><select value={filters.priority} onChange={(event) => setFilters((current) => ({ ...current, priority: event.target.value }))}>{PRIORITY_OPTIONS.map((option) => <option key={option || "all"} value={option}>{option || "alle"}</option>)}</select></label>
          <label><span>Rolle</span><select value={filters.reporter_role} onChange={(event) => setFilters((current) => ({ ...current, reporter_role: event.target.value }))}>{ROLE_OPTIONS.map((option) => <option key={option || "all"} value={option}>{option || "alle"}</option>)}</select></label>
          <label><span>Modul</span><select value={filters.module_key} onChange={(event) => setFilters((current) => ({ ...current, module_key: event.target.value }))}>{MODULE_OPTIONS.map((option) => <option key={option || "all"} value={option}>{option ? humanize(option) : "alle"}</option>)}</select></label>
          <label><span>Abschnitt</span><input value={filters.section_key} onChange={(event) => setFilters((current) => ({ ...current, section_key: event.target.value }))} placeholder="z. B. languages" /></label>
          <label><span>Anliegenart</span><select value={filters.issue_type} onChange={(event) => setFilters((current) => ({ ...current, issue_type: event.target.value }))}>{ISSUE_OPTIONS.map((option) => <option key={option || "all"} value={option}>{option ? humanize(option) : "alle"}</option>)}</select></label>
          <label><span>Kanal</span><select value={filters.channel} onChange={(event) => setFilters((current) => ({ ...current, channel: event.target.value }))}>{CHANNEL_OPTIONS.map((option) => <option key={option || "all"} value={option}>{option || "alle"}</option>)}</select></label>
          <label><span>Von</span><input type="date" value={filters.date_from} onChange={(event) => setFilters((current) => ({ ...current, date_from: event.target.value }))} /></label>
          <label><span>Bis</span><input type="date" value={filters.date_to} onChange={(event) => setFilters((current) => ({ ...current, date_to: event.target.value }))} /></label>
        </div>
        <div className="admin-kvp-filterbar__actions">
          <Button className="admin-core-button" onClick={applyFilters}>Filter anwenden</Button>
          <Button variant="outline" className="admin-core-button admin-core-button--secondary" onClick={resetFilters}>Zuruecksetzen</Button>
        </div>
      </section>

      {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}

      <div className="admin-kvp-layout">
        <section className="admin-kvp-panel">
          <div className="admin-kvp-panel__head">
            <div>
              <span className="admin-core-kicker">Arbeitsvorrat</span>
              <h3>Eingegangene Meldungen</h3>
            </div>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <strong>{rows.length}</strong>}
          </div>
          <div className="admin-kvp-list">
            {!loading && !rows.length ? (
              <div className="admin-kvp-empty">Keine Meldungen fuer den aktuellen Filter.</div>
            ) : null}
            {rows.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`admin-kvp-list__item${String(entry.id) === String(selectedId) ? " is-active" : ""}`}
                onClick={() => setSelectedId(String(entry.id))}
              >
                <div className="admin-kvp-list__top">
                  <span className={`admin-kvp-pill is-${String(entry.priority || "").toLowerCase()}`}>{entry.priority}</span>
                  <span className="admin-kvp-pill is-neutral">{entry.status}</span>
                  <span className="admin-kvp-pill is-neutral">{humanize(entry.module_key)}</span>
                </div>
                <strong>{entry.public_id}</strong>
                <p>{entry.description}</p>
                <div className="admin-kvp-list__meta">
                  <span>{entry.record_label || entry.section_key || "ohne Eintrag"}</span>
                  <span>{formatDateTime(entry.latest_activity_at)}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="admin-kvp-panel admin-kvp-panel--detail">
          <div className="admin-kvp-panel__head">
            <div>
              <span className="admin-core-kicker">Detail</span>
              <h3>{selectedReport?.public_id || "Meldung waehlen"}</h3>
            </div>
          </div>

          {detailLoading ? <div className="admin-kvp-empty">Detail wird geladen...</div> : null}
          {detailError ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{detailError}</div> : null}

          {!detailLoading && !selectedReport ? (
            <div className="admin-kvp-empty">Links eine Meldung auswaehlen, um Kontext und Verlauf zu sehen.</div>
          ) : null}

          {selectedReport ? (
            <div className="admin-kvp-detail">
              <div className="admin-kvp-detail__block">
                <div className="admin-kvp-detail__top">
                  <div>
                    <strong>{humanize(selectedReport.issue_type)}</strong>
                    <p>{selectedReport.description}</p>
                  </div>
                  <div className="admin-kvp-detail__badges">
                    <span className={`admin-kvp-pill is-${String(selectedReport.priority || "").toLowerCase()}`}>{selectedReport.priority}</span>
                    <span className="admin-kvp-pill is-neutral">{selectedReport.status}</span>
                  </div>
                </div>
                <div className="admin-kvp-meta-grid">
                  <div><span>Reporter</span><strong>{selectedReport.reporter_userid}</strong></div>
                  <div><span>E-Mail</span><strong>{selectedReport.reporter_email || "n/a"}</strong></div>
                  <div><span>Bereich</span><strong>{humanize(selectedReport.module_key)}</strong></div>
                  <div><span>Abschnitt</span><strong>{selectedReport.section_key || "n/a"}</strong></div>
                  <div><span>Eintrag</span><strong>{selectedReport.record_label || "n/a"}</strong></div>
                  <div><span>Route</span><strong>{selectedReport.route_path}</strong></div>
                  <div><span>Auswirkung</span><strong>{humanize(selectedReport.impact_level)}</strong></div>
                  <div><span>Ziel erreicht</span><strong>{humanize(selectedReport.goal_reached)}</strong></div>
                  <div><span>Kontakt erlaubt</span><strong>{selectedReport.wants_contact ? "Ja" : "Nein"}</strong></div>
                  <div><span>Zuletzt aktiv</span><strong>{formatDateTime(selectedReport.latest_activity_at)}</strong></div>
                </div>
                {selectedReport.include_field_value && selectedReport.current_field_value ? (
                  <div className="admin-kvp-inline-note">
                    <span>Aktiver Feldwert</span>
                    <strong>{selectedReport.current_field_value}</strong>
                  </div>
                ) : null}
              </div>

              <div className="admin-kvp-detail__block">
                <div className="admin-kvp-two-col">
                  <label className="admin-kvp-field">
                    <span>Prioritaet</span>
                    <select value={priorityDraft} onChange={(event) => setPriorityDraft(event.target.value)}>
                      {PRIORITY_OPTIONS.slice(1).map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    <Button
                      className="admin-core-button"
                      disabled={busyAction === "priority"}
                      onClick={() => mutateDetail("priority", () => axios.patch(`${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}`, { priority: priorityDraft }, { headers: authHeaders() }))}
                    >
                      Prioritaet speichern
                    </Button>
                  </label>

                  <label className="admin-kvp-field">
                    <span>Zuweisung</span>
                    <input value={assignedDraft} onChange={(event) => setAssignedDraft(event.target.value)} placeholder="admin-userid" />
                    <div className="admin-kvp-inline-actions">
                      <Button
                        className="admin-core-button"
                        disabled={busyAction === "assign"}
                        onClick={() => mutateDetail("assign", () => axios.post(`${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}/assign`, { assigned_userid: assignedDraft }, { headers: authHeaders() }))}
                      >
                        Zuweisen
                      </Button>
                      <Button
                        variant="outline"
                        className="admin-core-button admin-core-button--secondary"
                        disabled={busyAction === "assign-self"}
                        onClick={() => {
                          const adminUserid = getStoredIdentity().userid || "";
                          setAssignedDraft(adminUserid);
                          return mutateDetail("assign-self", () => axios.post(`${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}/assign`, { assigned_userid: adminUserid }, { headers: authHeaders() }));
                        }}
                      >
                        Mir zuweisen
                      </Button>
                    </div>
                  </label>
                </div>

                <label className="admin-kvp-field">
                  <span>Status wechseln</span>
                  <select value={statusDraft} onChange={(event) => setStatusDraft(event.target.value)}>
                    {STATUS_OPTIONS.slice(1).map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                  <textarea
                    rows={3}
                    value={statusComment}
                    onChange={(event) => setStatusComment(event.target.value)}
                    placeholder="Kommentar zum Statuswechsel"
                  />
                  <Button
                    className="admin-core-button"
                    disabled={busyAction === "status"}
                    onClick={() => mutateDetail("status", () => axios.post(`${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}/status`, { status: statusDraft, comment: statusComment }, { headers: authHeaders() }))}
                  >
                    Status speichern
                  </Button>
                </label>

                <label className="admin-kvp-field">
                  <span>Interne Notiz</span>
                  <textarea
                    rows={4}
                    value={eventText}
                    onChange={(event) => setEventText(event.target.value)}
                    placeholder="Interne Einordnung oder Rueckfrage festhalten"
                  />
                  <Button
                    className="admin-core-button"
                    disabled={!eventText.trim() || busyAction === "note"}
                    onClick={() => mutateDetail("note", () => axios.post(`${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}/events`, { event_type: "comment", body: eventText.trim() }, { headers: authHeaders() }))}
                  >
                    Notiz speichern
                  </Button>
                </label>

                <label className="admin-kvp-field">
                  <span>Antwort an Applicant</span>
                  <textarea
                    rows={4}
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    placeholder="Diese Rueckmeldung wird direkt am KVP-Fall des Applicants sichtbar."
                  />
                  <div className="admin-kvp-inline-note">
                    <span>Sichtbarkeit</span>
                    <strong>Wird im Applicant-KVP-Dialog angezeigt.</strong>
                  </div>
                  <Button
                    className="admin-core-button"
                    disabled={!replyText.trim() || busyAction === "reply"}
                    onClick={() => mutateDetail("reply", () => axios.post(`${config.API_BASE_URL}/api/admin/kvp/reports/${selectedReport.id}/reply`, { body: replyText.trim(), notify_reporter: true }, { headers: authHeaders() }))}
                  >
                    Antwort senden
                  </Button>
                </label>
              </div>

              <div className="admin-kvp-detail__block">
                <div className="admin-kvp-detail__section-head">
                  <h4>Screenshots und Anhaenge</h4>
                  <span>{selectedReport.attachments?.length || 0}</span>
                </div>
                {!selectedReport.attachments?.length ? (
                  <div className="admin-kvp-empty admin-kvp-empty--compact">Kein Screenshot angehaengt.</div>
                ) : (
                  <div className="admin-kvp-attachments">
                    {selectedReport.attachments.map((attachment) => (
                      <article key={attachment.id} className="admin-kvp-attachment-card">
                        {attachmentPreviews[attachment.id] ? (
                          <img
                            src={attachmentPreviews[attachment.id]}
                            alt={attachment.label || attachment.original_name || "KVP-Screenshot"}
                            className="admin-kvp-attachment-card__preview"
                          />
                        ) : null}
                        <div className="admin-kvp-attachment-card__copy">
                          <strong>{attachment.label || attachment.original_name || attachment.file_name}</strong>
                          <span>{attachment.mime_type} · {Math.round((attachment.file_size || 0) / 1024)} KB</span>
                          <Button
                            variant="outline"
                            className="admin-core-button admin-core-button--secondary"
                            onClick={() => downloadAttachment(attachment)}
                          >
                            Herunterladen
                          </Button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>

              <div className="admin-kvp-detail__block">
                <div className="admin-kvp-detail__section-head">
                  <h4>Verlauf</h4>
                  <span>{selectedReport.events?.length || 0}</span>
                </div>
                <div className="admin-kvp-timeline">
                  {(selectedReport.events || []).map((entry) => (
                    <article key={entry.id} className="admin-kvp-timeline__item">
                      <div className="admin-kvp-timeline__meta">
                        <strong>{humanizeEventType(entry.event_type)}</strong>
                        <span>{entry.actor_userid || "system"} · {formatDateTime(entry.created_at)}</span>
                      </div>
                      <div className="admin-kvp-timeline__meta">
                        <span className={`admin-kvp-pill ${entry.visibility_scope === "reporter" ? "is-reporter" : "is-neutral"}`}>
                          {entry.visibility_scope === "reporter" ? "Applicant sichtbar" : "Intern"}
                        </span>
                      </div>
                      <p>{entry.body || "Ohne Kommentar"}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </AdminPageFrame>
  );
}
